import { cn } from '@repo/tailwind-config/tailwind.functions'
import { CastleIcon } from '@repo/ui/icons'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

import { getCenterCulturalOutings } from '../../../../api/centers/center-cultural-outings/get-center-cultural-outings'
import { updateCenterCulturalOutings } from '../../../../api/centers/center-cultural-outings/update-center-cultural-outing'
import { getCenter } from '../../../../api/centers/get-center'
import { getCulturalOutings } from '../../../../api/cultural-outings/get-cultural-outings'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { Center } from '../../../../lib/types/models/center/center'
import {
  CenterCulturalOuting,
  CenterCulturalOutingInput
} from '../../../../lib/types/models/center/center-cultural-outing'
import { CulturalOutingProperties } from '../../../../lib/types/models/cultural-outing/cultural-outing.properties'
import { DataProps } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import EditableTable, { EditableTableColumn } from '../../../elements/editable-table'
import { useCenterCosts } from './center.hooks'
import {
  centerCostsTableColorMapping,
  CenterCostsTableFooter,
  CenterCostsTableHeader,
  CenterTableSkeleton,
  UploadInvoiceComponent,
  userInvoiceColorMapping
} from './center.ui'
import {
  CenterCostsTableProps,
  centerCulturalOutingsEditableColumns,
  getCenterCulturalOutingsColumns,
  mapCenterCulturalOutingsToInput
} from './utils'

export function CenterCulturalOutingsTable(props: DataProps<Center, CenterCostsTableProps>) {
  const { mode, costType, currency } = props
  const { t } = useTranslate()
  const { user } = useAuth()
  const [editable, setEditable] = useState(false)
  const [culturalOutings, setCulturalOutings] = useState<CenterCulturalOutingInput[]>()

  const {
    id: centerId,
    data: center,
    refetch: refetchCenter
  } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { centerCostsView, lock, unlock, isPending, uploadInvoice, deleteInvoice } =
    useCenterCosts({
      centerId,
      costType,
      table: 'cultural-outings'
    })

  const { data: culturalOutingsData } = useQuery({
    queryKey: ['cultural-outings'],
    queryFn: () => getCulturalOutings({ version: 'LIGHT' })
  })

  const { data: centerCulturalOutings, refetch } = useQuery({
    queryKey: ['center-cultural-outings', centerId],
    queryFn: () => getCenterCulturalOutings(centerId)
  })

  const columns: EditableTableColumn<CenterCulturalOuting>[] = useMemo(
    () => [
      ...getCenterCulturalOutingsColumns(
        costType,
        currency,
        t,
        culturalOutingsData?.data?.map((co) => ({
          label: CulturalOutingProperties.getLabel(co, t),
          value: co.id
        })) ?? [],
        centerCulturalOutingsEditableColumns[user.role]
      ),
      ...(costType === 'final-cost'
        ? ([
            {
              accessorKey: 'invoices',
              header: t('center.costs.tefl.teachers.and.leaders.table.header.upload.invoices'),
              centerCell: true,
              cell: ({ row }) => (
                <UploadInvoiceComponent
                  variant={userInvoiceColorMapping[user.role]}
                  invoice={row.original.invoice}
                  readOnly={(!editable && mode === 'action-edit') || mode === 'readonly'}
                  disabled={isPending}
                  onDelete={async () => {
                    await deleteInvoice.mutateAsync(row.original.id)
                    refetch()
                  }}
                  onChange={async (file) => {
                    await uploadInvoice.mutateAsync({ id: row.original.id, file })
                    refetch()
                  }}
                />
              )
            }
          ] as EditableTableColumn<CenterCulturalOuting>[])
        : [])
    ],
    [
      costType,
      currency,
      t,
      culturalOutingsData,
      user.role,
      editable,
      mode,
      isPending,
      uploadInvoice.mutateAsync,
      deleteInvoice.mutateAsync
    ]
  )

  const handleDataChange = useCallback(
    (data: CenterCulturalOuting[]) => {
      const mappedData = data.map((item) => mapCenterCulturalOutingsToInput(item, costType))
      if (mode === 'free-edit') {
        updateCenterCulturalOutings(centerId, mappedData)
          .then(() => {
            refetch()
            centerCostsView.refetch()
            setCulturalOutings(undefined)
          })
          .catch(showAxiosError)
      } else setCulturalOutings(mappedData)
    },
    [costType, mode, mapCenterCulturalOutingsToInput, centerId, centerCostsView.refetch]
  )

  if (!centerCulturalOutings || !center) return <CenterTableSkeleton />
  return (
    <div>
      <CenterCostsTableHeader
        mode={mode}
        icon={CastleIcon}
        title={t('center.costs.cultural.outings.title')}
        onCancel={() => setCulturalOutings(undefined)}
        onEditableChange={setEditable}
        validateOptions={{
          validate: costType === 'final-cost',
          locked: center.isHostFamiliesLocked,
          onLock: lock.mutateAsync,
          onUnlock: unlock.mutateAsync
        }}
        onSave={async () => {
          if (culturalOutings) {
            await updateCenterCulturalOutings(centerId, culturalOutings)
            await Promise.all([
              refetch(),
              centerCostsView.refetch(),
              setCulturalOutings(undefined)
            ])
            await Promise.all([refetch(), centerCostsView.refetch(), refetchCenter?.()])
          }
        }}
      />

      <EditableTable<CenterCulturalOuting>
        disableHover
        isEditable={editable}
        data={centerCulturalOutings.data}
        columns={columns}
        footerRowClassName={cn({ 'bg-white': costType === 'final-cost' })}
        onDataChange={handleDataChange}
        footer={
          <CenterCostsTableFooter
            currency={currency}
            costType={costType}
            textColor={centerCostsTableColorMapping[user.role][costType].textColor}
            variant={centerCostsTableColorMapping[user.role][costType].variant}
            label={
              costType === 'final-cost'
                ? t('center.final.costs.fees')
                : t(
                    'center.costs.cultural.outings.table.footer.total.total.transportation.coaches.booked'
                  )
            }
            total={centerCostsView.data?.totalCulturalOutingCost}
            initialAmount={centerCostsView.data?.totalCulturalOutingCost}
            finalAmount={centerCostsView.data?.finalTotalCulturalOutingCost}
            balance={centerCostsView.data?.culturalOutingBalance}
          />
        }
      />
    </div>
  )
}
